import 'moment/locale/hu'

/**
 * 语言和 Moment.js locale 映射工具
 * 
 * 这个文件提供了项目支持的所有语言与 moment.js locale 代码之间的映射关系
 * 避免在各个组件中重复定义语言映射，便于统一维护
 */

/**
 * i18n 语言代码到 moment.js locale 代码的映射
 * 基于项目 i18n/index.js 中定义的所有支持语言
 */
export const LOCALE_MAP = {
  'zh-CN': 'zh-cn',     // 简体中文
  'zh-TW': 'zh-tw',     // 繁体中文
  'ar': 'ar',           // 阿拉伯语
  'en': 'en',           // 英文
  'es': 'es',           // 西班牙语
  'fr': 'fr',           // 法语
  'id': 'id',           // 印尼语
  'ja': 'ja',           // 日语
  'ko': 'ko',           // 韩语
  'pt': 'pt',           // 葡萄牙语
  'ru': 'ru',           // 俄语
  'th': 'th',           // 泰语
  'he': 'he',           // 希伯来语
  'tr': 'tr',           // 土耳其语
  'hi': 'hi',           // 印地语
  'vi': 'vi',           // 越南语
  'de': 'de',           // 德语
  'hu': 'hu',           // 匈牙利语
  'fi': 'fi',           // 芬兰语
  'no': 'nb',           // 挪威语
  'it': 'it',           // 意大利语
  'kk': 'kk',           // 哈萨克斯坦语
  'ms': 'ms',           // 马来语
}

/**
 * 获取 moment.js 对应的 locale 代码
 * @param {string} i18nLocale - i18n 语言代码
 * @returns {string} moment.js locale 代码
 */
export function getMomentLocale(i18nLocale) {
  return LOCALE_MAP[i18nLocale]
}

/**
 * 设置 moment.js 的 locale
 * @param {string} i18nLocale - i18n 语言代码
 * @param {object} moment - moment.js 实例
 */
export function setMomentLocale(i18nLocale, moment) {
  const momentLocale = getMomentLocale(i18nLocale)
  if (momentLocale && moment) {
    moment.locale(momentLocale)
  }
}

/**
 * 获取项目支持的所有语言代码列表
 * @returns {string[]} 支持的语言代码数组
 */
export function getSupportedLocales() {
  return Object.keys(LOCALE_MAP)
}

/**
 * 检查语言是否被支持
 * @param {string} locale - 语言代码
 * @returns {boolean} 是否支持该语言
 */
export function isLocaleSupported(locale) {
  return locale in LOCALE_MAP
}

/**
 * 日期格式配置映射
 * 为每种支持的语言定义月年显示格式
 */
export const DATE_FORMATS = {
  'zh-CN': 'YYYY年MM月',
  'zh-TW': 'YYYY年MM月',
  'en': 'MMMM YYYY',
  'ja': 'YYYY年MM月',
  'ko': 'YYYY년 MM월',
  'ar': 'MMMM YYYY',
  'es': 'MMMM YYYY',
  'fr': 'MMMM YYYY',
  'de': 'MMMM YYYY',
  'hu': 'YYYY. MMMM',   // 匈牙利语
  'it': 'MMMM YYYY',
  'ru': 'MMMM YYYY',
  'id': 'MMMM YYYY',      // 印尼语
  'pt': 'MMMM YYYY',      // 葡萄牙语
  'th': 'MMMM YYYY',      // 泰语
  'he': 'MMMM YYYY',      // 希伯来语
  'tr': 'MMMM YYYY',      // 土耳其语
  'hi': 'MMMM YYYY',      // 印地语
  'vi': 'MMMM YYYY',      // 越南语
  'kk': 'YYYY MMMM',      // 哈萨克斯坦语
  'ms': 'MMMM YYYY'       // 马来语
}

/**
 * 获取指定语言的日期格式
 * @param {string} locale - 语言代码
 * @returns {string} 日期格式字符串
 */
export function getDateFormat(locale) {
  return DATE_FORMATS[locale]
}
